import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import AppLayout from '../components/AppLayout';
import api, { courseAPI } from '../services/api';
import { singleEnrolledCoursePath } from '../utils/studentNavigation';
import '../styles/components/_dashboard.scss';
import { getLocalizedText } from '../utils/i18n';

interface AssessmentFollowupSummary {
  id: string;
  label: any;
  unlockDate: string | null;
}

interface AssessmentFollowupTimelineRow {
  id: string;
  label: any;
  order: number;
  submitted: boolean;
  availableNow: boolean;
  /** Date de suivi atteinte mais diagnostic initial non fait. */
  pendingDiagnostic?: boolean;
  unlockDate: string | null;
}

interface AssessmentSummary {
  enabled: boolean;
  diagnosticTodo: boolean;
  finalTodo: boolean;
  finalSubmitted?: boolean;
  finalImpactUnlockRule?: 'after-last-workshop' | 'when-course-complete';
  followupTodoCount: number;
  followupsAvailable?: AssessmentFollowupSummary[];
  /** Tous les suivis M+ configurés (pour la timeline tableau de bord). */
  followupsAll?: AssessmentFollowupTimelineRow[];
}

interface Course {
  id: string;
  title: { [key: string]: string };
  description: { [key: string]: string };
  availableLanguages?: string[];
  defaultLanguage?: string;
  image?: string;
  progress: number;
  status: 'active' | 'completed' | 'locked';
  accessUntil: string;
  chapters: number;
  completedChapters: number;
  /** ISO — déblocage formation (groupe), présent si défini côté admin */
  unlockDate?: string | null;
  /** true si unlockDate est dans le futur */
  courseLocked?: boolean;
  diagnosticImpactEnabled?: boolean;
  assessmentSummary?: AssessmentSummary;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseImageUrls, setCourseImageUrls] = useState<{ [courseId: string]: string }>({});

  useEffect(() => {
    loadUserData();
    loadCourses();
  }, []);

  const loadUserData = () => {
    const userData = localStorage.getItem('student_user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate('/login');
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getCourses();
      console.log('📚 Courses loaded:', response.courses);
      const list = response.courses || [];

      const onlyCoursePath = singleEnrolledCoursePath(list);
      if (onlyCoursePath) {
        navigate(onlyCoursePath, { replace: true });
        return;
      }

      if (list.length > 0) {
        setCourses(list);
      } else {
        console.warn('⚠️ No courses found. Make sure you are enrolled in at least one course.');
        setCourses([]);
      }
    } catch (error: any) {
      console.error('❌ Error loading courses:', error);
      console.error('Error details:', error.response?.data);

      // If authentication error, the interceptor will handle it
      // But we should also clear local state
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('student_token');
        localStorage.removeItem('student_user');
        setLoading(false);
        navigate('/login');
        return;
      }

      // Don't use mock data - show empty state instead
      setCourses([]);
    }
    setLoading(false);
  };


  const handleCourseClick = (courseId: string) => {
    navigate(`/course/${courseId}`);
  };

  const buildImageUrl = (imageUrl: string): string => {
    if (!imageUrl || imageUrl.trim() === '') {
      return '';
    }

    // If it's an absolute URL (http/https), use as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    // For relative URLs, build the full URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const baseUrl = API_BASE_URL.replace('/api', ''); // Remove /api to get server base URL

    let fullUrl: string;
    if (imageUrl.startsWith('/')) {
      // Route media paths through API so CloudFront forwards to backend.
      fullUrl = imageUrl.startsWith('/uploads/')
        ? `${API_BASE_URL}${imageUrl}`
        : `${baseUrl}${imageUrl}`;
    } else {
      // Relative URL without /, assume it's in /uploads served by API.
      fullUrl = `${API_BASE_URL}/uploads/${imageUrl}`;
    }

    return fullUrl;
  };

  // Load course images with authentication
  useEffect(() => {
    if (courses.length === 0) return;

    const loadCourseImages = async () => {
      const imageUrls: { [courseId: string]: string } = {};
      
      for (const course of courses) {
        if (course.image) {
          try {
            const token = localStorage.getItem('student_token');
            const imageUrl = course.image.startsWith('http')
              ? course.image
              : buildImageUrl(course.image);

            console.log('🖼️ Loading course image:', {
              courseId: course.id,
              original: course.image,
              builtUrl: imageUrl,
              hasToken: !!token
            });

            // Try to fetch image with authentication
            const response = await fetch(imageUrl, {
              headers: token ? {
                'Authorization': `Bearer ${token}`
              } : {}
            });

            if (response.ok) {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              imageUrls[course.id] = url;
              console.log('✅ Course image loaded successfully:', course.id);
            } else {
              console.error('❌ Failed to load course image:', {
                courseId: course.id,
                status: response.status,
                statusText: response.statusText
              });
              // Fallback to direct URL
              imageUrls[course.id] = imageUrl;
            }
          } catch (error) {
            console.error('❌ Error loading course image:', course.id, error);
            // Fallback to direct URL
            imageUrls[course.id] = buildImageUrl(course.image);
          }
        }
      }

      setCourseImageUrls(imageUrls);
    };

    loadCourseImages();

    // Cleanup: revoke blob URLs when component unmounts or courses change
    return () => {
      setCourseImageUrls(prevUrls => {
        Object.values(prevUrls).forEach(url => {
          if (url && url.startsWith('blob:')) {
            window.URL.revokeObjectURL(url);
          }
        });
        return {};
      });
    };
  }, [courses]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!loading && courses.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', mb: 2 }}>
          {t('dashboard.noCourses')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, textAlign: 'center' }}>
          {t('dashboard.noCoursesMessage')}
        </Typography>
      </Box>
    );
  }

  return (
    <AppLayout>
      <div className="dashboard__container">
        {/* Main Content */}
        <Box className="dashboard__content">
          {/* Greeting */}
          <Box className="dashboard__greeting">
            <p 
              className="dashboard__greeting-title"
            >
              {t('dashboard.greeting', { firstName: user?.firstName })}
            </p>
            <p 
              className="dashboard__greeting-subtitle"
            >
              {t('dashboard.coursesCount', { 
                count: courses.length, 
                plural: courses.length > 1 ? 's' : '' 
              })}
            </p>
          </Box>

          {/* Courses */}
          <Box className="dashboard__courses-container">
            {courses.map((course) => {
              const progressPct = Math.round(Number(course.progress ?? 0));
              const lockedByDate = course.courseLocked === true;
              const statusChipLabel = lockedByDate
                ? t('dashboard.statusComingSoon')
                : progressPct >= 100
                  ? t('dashboard.statusCompleted')
                  : t('dashboard.statusInProgress');
              return (
              <div
                key={course.id}
                className="dashboard__course-card"
              >
                <Box className="dashboard__course-card-inner">
                  {/* Course Image */}
                  {course.image && (() => {
                    const courseImage = course.image; // Store in const for TypeScript
                    return (
                      <Box className="dashboard__course-image-container">
                        <CardMedia
                          component="img"
                          height="200"
                          image={courseImageUrls[course.id] || buildImageUrl(courseImage)}
                          alt={getLocalizedText(course.title)}
                          className="dashboard__course-image"
                          onError={(e) => {
                            console.error('❌ Image load error for course:', course.id);
                            // Fallback to direct URL if blob fails
                            const target = e.target as HTMLImageElement;
                            const fallbackUrl = buildImageUrl(courseImage);
                            if (target.src !== fallbackUrl) {
                              target.src = fallbackUrl;
                            }
                          }}
                        />
                      </Box>
                    );
                  })()}
                  <div className="dashboard__course-details-container">
                  {/* Course Details */}
                  <div className="dashboard__course-details">
                    {/* Top Section */}
                    <div className="dashboard__course-details-top">
                      <p 
                        className="dashboard__course-title"
                      >
                        {getLocalizedText(course.title)}
                      </p>
                      
                      <Chip
                        label={statusChipLabel}
                        size="small"
                        className="dashboard__course-status-chip"
                        sx={{
                          backgroundColor: lockedByDate
                            ? 'warning.light'
                            : progressPct >= 100
                              ? 'success.light'
                              : 'info.light',
                          color: 'text.primary',
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      />
                      {Array.isArray(course.availableLanguages) && course.availableLanguages.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                          {course.availableLanguages.map((lang) => (
                            <Chip
                              key={lang}
                              size="small"
                              label={lang.toUpperCase()}
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                borderColor: 'rgba(255,255,255,0.45)',
                                color: 'rgba(255,255,255,0.9)'
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      {(() => {
                        const s = course.assessmentSummary;
                        if (!s?.enabled || s.finalSubmitted) return null;
                        if (s.diagnosticTodo) return null;
                        const rule = s.finalImpactUnlockRule;
                        if (!rule) return null;
                        return (
                          <Typography
                            variant="body2"
                            component="p"
                            sx={{
                              mt: 0.5,
                              mb: 0,
                              color: 'rgba(255,255,255,0.78)',
                              fontSize: '0.8125rem',
                              lineHeight: 1.35,
                              fontWeight: 500,
                            }}
                          >
                            {rule === 'after-last-workshop'
                              ? t('assessment.final.unlockHintWorkshop')
                              : t('assessment.final.unlockHintCourseEnd')}
                          </Typography>
                        );
                      })()}
                    </div>

                    {/* Bottom Section */}
                    <div className="dashboard__course-details-right">
                      {/* Progress */}
                      <div className="dashboard__progress-section">
                        <div className="dashboard__progress-header">
                          <p className="dashboard__progress-percentage">
                            {progressPct} %
                          </p>
                        </div>
                        <LinearProgress
                          variant="determinate"
                          value={progressPct}
                          className="dashboard__progress-bar"
                          sx={{
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 'var(--brand-shell-bg, #13322B)'
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="dashboard__course-details-bottom">
                    {!lockedByDate && (
                      <Button
                        onClick={() => handleCourseClick(course.id)}
                        className="button-primary dashboard__resume-button"
                      >
                        {progressPct === 0
                          ? t('courseDetail.start')
                          : progressPct >= 100
                            ? t('courseDetail.review')
                            : t('dashboard.resumeButton')}
                      </Button>
                    )}
                  </div>
                    </div>
                </Box>
              </div>
              );
            })}
          </Box>
        </Box>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
